import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { axiosInstance } from '../helpers'
import { normalizeListResponse } from '../helpers/companyDocumentList'
import { CompanyDocument } from '../models/Company/types'

const QUERY_KEY = ['orgCompanyDocuments']
const COVERAGE_QUERY_KEY = ['organizationDocumentTypeCoverage']

export interface OrgDocumentParams {
    organization?: number | null
    /** When true, list only documents with no document_type. */
    generalOnly?: boolean
    /** Filter to this organization document type id. */
    documentTypeId?: number | null
}

export interface UpdateCompanyDocumentInput {
    document_type?: number | null
    name?: string
    ai_summary?: string | null
    companies?: number[]
}

export const useOrgCompanyDocuments = (params?: OrgDocumentParams) => {
    const queryClient = useQueryClient()
    const [processingIds, setProcessingIds] = useState<Set<number>>(new Set())

    const buildListQueryString = () => {
        if (!params?.organization) return ''
        const searchParams = new URLSearchParams()
        searchParams.append('organization', params.organization.toString())
        if (params.generalOnly) {
            searchParams.append('general_only', 'true')
        }
        if (params.documentTypeId != null) {
            searchParams.append('document_type', String(params.documentTypeId))
        }
        const queryString = searchParams.toString()
        return queryString ? `?${queryString}` : ''
    }

    // Build query string for detail mutations (organization only, no list filters)
    const buildDetailQueryString = () => {
        if (!params?.organization) return ''
        return `?organization=${params.organization}`
    }

    /** POST uses organization only (list filters do not apply to create). */
    const buildPostQueryString = () => {
        if (!params?.organization) return ''
        const searchParams = new URLSearchParams()
        searchParams.append('organization', params.organization.toString())
        const queryString = searchParams.toString()
        return queryString ? `?${queryString}` : ''
    }

    const {
        data: documents,
        isLoading: isLoadingDocuments,
        error: errorDocuments,
        refetch: refetchDocuments,
    } = useQuery<CompanyDocument[], Error>({
        queryKey: [
            ...QUERY_KEY,
            params?.organization,
            params?.generalOnly,
            params?.documentTypeId,
        ],
        queryFn: async () => {
            const res = await axiosInstance.get<unknown>(
                `/company/company_document/${buildListQueryString()}`,
            )
            return normalizeListResponse<CompanyDocument>(res.data)
        },
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        enabled: !!params?.organization,
    })

    const createDocument = useMutation({
        mutationFn: async ({
            file,
            company_ids,
            document_type,
        }: {
            file: File
            company_ids: number[]
            document_type?: number | null
        }) => {
            const formData = new FormData()
            formData.append('file', file)
            formData.append('company_ids', JSON.stringify(company_ids))
            if (document_type != null && document_type !== undefined) {
                formData.append('document_type', String(document_type))
            }

            return axiosInstance.post<CompanyDocument>(
                `/company/company_document/${buildPostQueryString()}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                },
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            queryClient.invalidateQueries({ queryKey: COVERAGE_QUERY_KEY })
            toast.success('Document uploaded successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to upload document'
            toast.error(errorMessage)
        },
    })

    const updateDocument = useMutation({
        mutationFn: async ({
            documentId,
            data,
        }: {
            documentId: number
            data: UpdateCompanyDocumentInput
        }) =>
            axiosInstance.patch<CompanyDocument>(
                `/company/company_document/${documentId}/${buildDetailQueryString()}`,
                data,
            ),
        onSuccess: async (_data, variables) => {
            await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            await queryClient.invalidateQueries({
                queryKey: COVERAGE_QUERY_KEY,
            })
            if (
                Object.prototype.hasOwnProperty.call(
                    variables.data,
                    'document_type',
                )
            ) {
                await queryClient.refetchQueries({ queryKey: QUERY_KEY })
            }
            toast.success('Document updated successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to update document'
            toast.error(errorMessage)
        },
    })

    const processDocument = useMutation({
        mutationFn: async (documentId: number) => {
            setProcessingIds((prev) => new Set(prev).add(documentId))
            return axiosInstance.post(
                `/company/company_document/${documentId}/process_document/${buildDetailQueryString()}`,
            )
        },
        onSuccess: (_, documentId) => {
            setProcessingIds((prev) => {
                const next = new Set(prev)
                next.delete(documentId)
                return next
            })
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            toast.success('Document analyzed successfully')
        },
        onError: (error: any, documentId) => {
            setProcessingIds((prev) => {
                const next = new Set(prev)
                next.delete(documentId)
                return next
            })
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to analyze document'
            toast.error(errorMessage)
        },
    })

    const deleteDocument = useMutation({
        mutationFn: async (documentId: number) => {
            return axiosInstance.delete(
                `/company/company_document/${documentId}/${buildDetailQueryString()}`,
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            queryClient.invalidateQueries({ queryKey: COVERAGE_QUERY_KEY })
            toast.success('Document deleted successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to delete document'
            toast.error(errorMessage)
        },
    })

    const uploadDocuments = async (
        files: File[],
        company_ids: number[],
        document_type?: number | null,
    ) => {
        const results = await Promise.allSettled(
            files.map((file) =>
                createDocument.mutateAsync({
                    file,
                    company_ids,
                    document_type,
                }),
            ),
        )
        const successCount = results.filter((r) => r.status === 'fulfilled')
            .length
        const failCount = results.filter((r) => r.status === 'rejected').length
        if (failCount > 0 && successCount > 0) {
            toast.warning(
                `Uploaded ${successCount} document(s), ${failCount} failed`,
            )
        } else if (failCount > 0 && successCount === 0) {
            toast.error('Failed to upload documents')
        }
        return results
    }

    return {
        documents: documents ?? [],
        isLoadingDocuments,
        errorDocuments,
        refetchDocuments,
        createDocument,
        updateDocument,
        uploadDocuments,
        processDocument,
        deleteDocument,
        isUploading: createDocument.isLoading,
        isUpdating: updateDocument.isLoading,
        isProcessing: processDocument.isLoading,
        isDeleting: deleteDocument.isLoading,
        processingIds,
    }
}
